import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import { StoreState } from "../../redux/configure-store";
import PageTitle from "../components/PageTitle";
import AdminTimesheetsTable from '../timesheets/AdminTimesheetsTable';
import { Timesheet } from "../types";
import Loading from '../../common/components/Loading'
import {
  dispatchAdminSetSlectedTimesheets,
  dispatchAdminUnsetSlectedTimesheets
} from '../../admin/admin-actions'
import {
  calculateTotalHoursForTimesheets,
  calculateTotalNumberOfHoursForTimesheet,
  formatShortDate,
} from "../../common/utils"
import dayjs from "../../common/date";
import { backgrounds, borders } from "../../theme/theme"

function AdminEngagementsView(props: any) {
  const {
    engagement,
    selectedTimesheetIds,
    allTimesheets,
    navigateToAdminEngagementsCreateInvoice,
    dispatchAdminSetSlectedTimesheets: _dispatchAdminSetSlectedTimesheets,
    dispatchAdminUnsetSlectedTimesheets: _dispatchAdminUnsetSlectedTimesheets,
  } = props;

  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [selectedTimesheets, setSelectedTimesheets] = useState<Timesheet[]>([]);
  const [totalNumberOfHours, setTotalNumberOfHours] = useState<string>('0');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    _dispatchAdminUnsetSlectedTimesheets();
  }, [_dispatchAdminUnsetSlectedTimesheets])

  useEffect(() => {
    if (!engagement) {
      return;
    }
    setTimesheets(allTimesheets.filter(
      (t: Timesheet) => t.engagement.id === engagement.id
    ));
  }, [engagement, allTimesheets]);

  useEffect(() => {
    if (!engagement || !timesheets.length) {
      return;
    }

    if (!selectedTimesheetIds.length) {
      setSelectedTimesheets([]);
      setTotalNumberOfHours('0');
      return;
    }
    const _selectedTimesheets = selectedTimesheetIds.map((id: number) => timesheets.find((t: Timesheet) => t.id === id));
    setSelectedTimesheets(_selectedTimesheets);

    setTotalNumberOfHours(calculateTotalHoursForTimesheets(_selectedTimesheets));
  }, [selectedTimesheetIds, timesheets, engagement]);

  if (!engagement) {
    return <Loading></Loading>
  }

  return (
    <div>
      <PageTitle>Engagement Details</PageTitle>

      {/* Engagement Info Section */}
      <Box sx={{ mb: 3, p: 2, backgroundColor: backgrounds.page, borderRadius: 1 }}>
        <Grid container spacing={3}>
          {/* Engagement Details */}
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
              Engagement Info
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Developer:</strong> {engagement.user.firstName} {engagement.user.lastName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Client:</strong> {engagement.clientName}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Rate:</strong> {engagement.currency} {engagement.hourlyRate}/hr
              </Typography>
              <Typography variant="body2">
                <strong>Hours/Week:</strong> {engagement.numberOfHoursPerWeek}
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigateToAdminEngagementsCreateInvoice(engagement.id)}
            >
              Create Invoice
              {selectedTimesheetIds.length > 0 && (
                <Chip
                  label={`${selectedTimesheetIds.length} selected`}
                  size="small"
                  sx={{ ml: 1, backgroundColor: "rgba(255,255,255,0.2)", color: "white" }}
                />
              )}
            </Button>
          </Grid>

          {/* Selected Timesheets Preview */}
          <Grid item xs={12} md={8}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
              Selected Timesheets Preview
            </Typography>
            <Paper
              variant="outlined"
              sx={{ backgroundColor: "white", position: "relative" }}
            >
              {!selectedTimesheets.length ? (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Click on timesheets below to select them for invoice creation...
                  </Typography>
                </Box>
              ) : (() => {
                const timeFormat = selectedTimesheets[0].timeFormat;
                const periodSummaries = selectedTimesheets.map((ts) => ({
                  start: formatShortDate(ts.startDate),
                  end: formatShortDate(ts.endDate),
                  hours: calculateTotalNumberOfHoursForTimesheet(ts.details, ts.timeFormat),
                }));
                const allEntries = selectedTimesheets.flatMap((ts) => ts.details);

                const formatDateRow = (entry: { date: string; numberOfHours: string }) => {
                  const d = dayjs(entry.date).utc().format("ddd MMM D YYYY");
                  const hours = entry.numberOfHours.replace(/^0+(\d+:)/, "$1");
                  return d.padEnd(16) + hours.padStart(5);
                };

                const developerName = `${engagement.user.firstName} ${engagement.user.lastName}`;
                const bodyLines = [
                  `Format: ${timeFormat}`,
                  `Total Hours: ${totalNumberOfHours}`,
                  ...periodSummaries.map((p) => `${p.start} - ${p.end} (${p.hours} hrs)`),
                  "",
                  ...allEntries.map(formatDateRow),
                ];

                const handleCopy = () => {
                  const plainText = [developerName, ...bodyLines].join("\n");
                  const htmlBody = bodyLines.join("\n");
                  const html = `<strong>${developerName}</strong><br><pre style="font-family:'Courier New',monospace;font-size:13px;margin:0;line-height:1.5;">${htmlBody}</pre>`;
                  const CI = (window as any).ClipboardItem;
                  if (CI && navigator.clipboard.write) {
                    navigator.clipboard.write([
                      new CI({
                        "text/plain": new Blob([plainText], { type: "text/plain" }),
                        "text/html": new Blob([html], { type: "text/html" }),
                      }),
                    ]).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
                      .catch(() => { navigator.clipboard.writeText(plainText).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); });
                  } else {
                    navigator.clipboard.writeText(plainText).then(() => {
                      setCopied(true); setTimeout(() => setCopied(false), 2000);
                    });
                  }
                };



                return (
                  <>
                    <Tooltip title={copied ? "Copied!" : "Copy to clipboard"} placement="top">
                      <IconButton
                        size="small"
                        onClick={handleCopy}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          color: copied ? "success.main" : "text.secondary",
                        }}
                      >
                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                    <Box
                      sx={{
                        p: 2,
                        pr: 5,
                        maxHeight: 220,
                        overflow: "auto",
                      }}
                    >
                      <pre style={{
                        margin: 0,
                        fontFamily: "monospace",
                        fontSize: "12px",
                        whiteSpace: "pre",
                        lineHeight: 1.6,
                      }}>
                        {bodyLines.map((line, i) => (
                          <span key={i} style={line.startsWith("Total Hours:") ? { fontWeight: "bold" } : {}}>
                            {line}{"\n"}
                          </span>
                        ))}
                      </pre>
                    </Box>
                  </>
                );
              })()}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Timesheets Table Section */}
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
        Timesheets
      </Typography>
      <Paper elevation={0} sx={{ width: "100%", overflowX: "auto", border: `1px solid ${borders.light}`, borderRadius: 2 }}>
        <AdminTimesheetsTable
          selectedTimesheetIds={selectedTimesheetIds}
          onRowClick={(timesheetId: number) => {
            const timesheet = timesheets.find(t => t.id === timesheetId);
            if (selectedTimesheets.length && timesheet?.timeFormat !== selectedTimesheets[0].timeFormat) {
              alert('Error. Selected timesheets must have the same time format. You cannot select a `decimal` and `minutes` timesheet.')
              return;
            }
            const ids = selectedTimesheetIds.includes(timesheetId)
              ? selectedTimesheetIds.filter((id: number) => id !== timesheetId)
              : [...selectedTimesheetIds, timesheetId].sort()
            _dispatchAdminSetSlectedTimesheets(ids)
          }}
          timesheets={timesheets}
        />
      </Paper>
    </div>
  );
}

const mapStateToProps = (storeState: StoreState) => ({
  allTimesheets: storeState.admin.timesheets,
  selectedTimesheetIds: storeState.admin.selectedTimesheetIds
});

export default connect(
  mapStateToProps,
  {
    dispatchAdminSetSlectedTimesheets,
    dispatchAdminUnsetSlectedTimesheets,
  }
)(AdminEngagementsView);
